/**
 * Deteccion de conectividad.
 *
 * `navigator.onLine` NO basta: en Windows y Android devuelve `true` cuando hay wifi
 * aunque el wifi no llegue a internet (un portal cautivo, por ejemplo). Por eso se
 * combina con un sondeo real al health check de la API, que es la unica prueba de
 * que el servidor esta realmente alcanzable.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BASE_URL } from '../api/client.js';

export type ConnectionState = 'EN_LINEA' | 'SIN_CONEXION' | 'COMPROBANDO';

const PROBE_INTERVAL_MS = 30_000;
const PROBE_TIMEOUT_MS = 5_000;

export function useConnectivity(): { state: ConnectionState; check: () => Promise<boolean> } {
  const [state, setState] = useState<ConnectionState>(
    typeof navigator !== 'undefined' && !navigator.onLine ? 'SIN_CONEXION' : 'COMPROBANDO',
  );
  const mounted = useRef(true);

  const check = useCallback(async (): Promise<boolean> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      if (mounted.current) setState('SIN_CONEXION');
      return false;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
      const response = await fetch(`${BASE_URL}/health`, {
        signal: controller.signal,
        cache: 'no-store',
      });
      const online = response.ok;
      if (mounted.current) setState(online ? 'EN_LINEA' : 'SIN_CONEXION');
      return online;
    } catch {
      if (mounted.current) setState('SIN_CONEXION');
      return false;
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void check();

    // Los eventos del navegador dan reaccion inmediata; el sondeo periodico detecta
    // los casos que los eventos no ven (portal cautivo, servidor caido).
    const onOnline = (): void => void check();
    const onOffline = (): void => setState('SIN_CONEXION');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const interval = window.setInterval(() => void check(), PROBE_INTERVAL_MS);

    return () => {
      mounted.current = false;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.clearInterval(interval);
    };
  }, [check]);

  return { state, check };
}
